import { X } from "@phosphor-icons/react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "@krea/shared/utils";

const ResponsiveDialog = DialogPrimitive.Root;

const ResponsiveDialogTrigger = DialogPrimitive.Trigger;

const ResponsiveDialogClose = DialogPrimitive.Close;

const ResponsiveDialogPortal = DialogPrimitive.Portal;

const ResponsiveDialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    className={cn(
      "tw-inset-0 tw-fixed tw-z-[400000] tw-bg-white/80 tw-backdrop-blur-sm data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=closed]:tw-fade-out-0 data-[state=open]:tw-fade-in-0",
      className,
    )}
    {...props}
    ref={ref}
  />
));
ResponsiveDialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const ResponsiveDialogVariants = cva(
  cn(
    "tw-fixed tw-z-[400001] tw-gap-4 tw-bg-white tw-p-6 tw-shadow-lg tw-transition tw-ease-in-out data-[state=open]:tw-animate-in data-[state=closed]:tw-animate-out data-[state=closed]:tw-duration-300 data-[state=open]:tw-duration-500 tw-overflow-y-auto",
    "lg:tw-left-[50%] lg:tw-top-[50%] lg:tw-grid lg:tw-w-full lg:tw-max-w-2xl lg:tw-translate-x-[-50%] lg:tw-translate-y-[-50%] lg:tw-border lg:tw-duration-200 lg:data-[state=open]:tw-animate-in lg:data-[state=closed]:tw-animate-out lg:data-[state=closed]:tw-fade-out-0 lg:data-[state=open]:tw-fade-in-0 lg:data-[state=closed]:tw-zoom-out-95 lg:data-[state=open]:tw-zoom-in-95 lg:data-[state=closed]:tw-slide-out-to-left-1/2 lg:data-[state=closed]:tw-slide-out-to-top-[48%] lg:data-[state=open]:tw-slide-in-from-left-1/2 lg:data-[state=open]:tw-slide-in-from-top-[48%] lg:tw-rounded-xl",
  ),
  {
    variants: {
      side: {
        top: "tw-inset-x-0 tw-top-0 tw-max-h-[90%] tw-rounded-b-xl tw-border-b data-[state=closed]:tw-slide-out-to-top data-[state=open]:tw-slide-in-from-top lg:tw-h-fit",
        bottom:
          "tw-inset-x-0 tw-bottom-0 tw-max-h-[90%] tw-rounded-t-xl tw-border-t data-[state=closed]:tw-slide-out-to-bottom data-[state=open]:tw-slide-in-from-bottom lg:tw-h-fit",
        left: "tw-inset-y-0 tw-left-0 tw-h-full tw-w-3/4 tw-rounded-r-xl tw-border-r data-[state=closed]:tw-slide-out-to-left data-[state=open]:tw-slide-in-from-left sm:tw-max-w-sm lg:tw-h-fit",
        right:
          "tw-inset-y-0 tw-right-0 tw-h-full tw-w-3/4 tw-rounded-l-xl tw-border-l data-[state=closed]:tw-slide-out-to-right data-[state=open]:tw-slide-in-from-right sm:tw-max-w-sm lg:tw-h-fit",
      },
    },
    defaultVariants: {
      side: "bottom",
    },
  },
);

interface IResponsiveDialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>,
    VariantProps<typeof ResponsiveDialogVariants> {
  elevatedStacking?: boolean;
}

const ResponsiveDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  IResponsiveDialogContentProps
>(
  (
    {
      side = "bottom",
      className,
      children,
      elevatedStacking = false,
      ...props
    },
    ref,
  ) => (
    <ResponsiveDialogPortal>
      <ResponsiveDialogOverlay
        className={cn(elevatedStacking && "tw-z-[500000]")}
      >
        <DialogPrimitive.Content
          ref={ref}
          className={cn(
            ResponsiveDialogVariants({ side }),
            elevatedStacking && "tw-z-[500001]",
            className,
          )}
          aria-describedby={undefined}
          {...props}
        >
          <DialogPrimitive.Title className="tw-sr-only">
            Dialog
          </DialogPrimitive.Title>
          {children}

          <ResponsiveDialogClose
            className="tw-absolute tw-right-4 tw-top-4 tw-rounded-sm tw-opacity-70 tw-ring-offset-white tw-transition-opacity hover:tw-opacity-100 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-primary focus:tw-ring-offset-2 disabled:tw-pointer-events-none data-[state=open]:tw-bg-gray-100"
            id="-default-dialog-close-icon"
          >
            <X className="tw-h-4 tw-w-4" />
            <span className="tw-sr-only">Close</span>
          </ResponsiveDialogClose>
        </DialogPrimitive.Content>
      </ResponsiveDialogOverlay>
    </ResponsiveDialogPortal>
  ),
);
ResponsiveDialogContent.displayName = DialogPrimitive.Content.displayName;

const ResponsiveDialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "tw-flex tw-flex-col tw-space-y-2 tw-text-center sm:tw-text-left",
      className,
    )}
    {...props}
  />
);
ResponsiveDialogHeader.displayName = "ResponsiveDialogHeader";

const ResponsiveDialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "tw-flex tw-flex-col-reverse sm:tw-flex-row sm:tw-justify-end sm:tw-space-x-2",
      className,
    )}
    {...props}
  />
);
ResponsiveDialogFooter.displayName = "ResponsiveDialogFooter";

const ResponsiveDialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("tw-text-lg tw-font-semibold tw-text-gray-900", className)}
    {...props}
  />
));
ResponsiveDialogTitle.displayName = DialogPrimitive.Title.displayName;

const ResponsiveDialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("tw-text-sm tw-text-gray-600", className)}
    {...props}
  />
));
ResponsiveDialogDescription.displayName =
  DialogPrimitive.Description.displayName;

const ResponsiveDialogContentPrimitive = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Content
    ref={ref}
    className={cn(className)}
    aria-describedby={undefined}
    {...props}
  />
));

export {
  ResponsiveDialog,
  ResponsiveDialogClose,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogOverlay,
  ResponsiveDialogPortal,
  ResponsiveDialogTitle,
  ResponsiveDialogTrigger,
  ResponsiveDialogContentPrimitive,
};
